import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { SkipLink } from '../a11y/SkipLink';
import { useAuth } from '../../providers/AuthProvider';

const NAV = [
  { to: '/portal', label: 'Dashboard', end: true },
  { to: '/portal/lease', label: 'Lease', end: false },
  { to: '/portal/ledger', label: 'Ledger', end: false },
  { to: '/portal/pay', label: 'Pay rent', end: false },
  { to: '/portal/documents', label: 'Documents', end: false },
  { to: '/portal/maintenance', label: 'Maintenance', end: false },
  { to: '/portal/messages', label: 'Messages', end: false },
  { to: '/portal/payment-methods', label: 'Payment methods', end: false },
];

/** Best-effort page title for the current path, from NAV (longest/most-specific match wins). */
function pageTitle(pathname: string): string {
  const hit = NAV.filter((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to))).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
  return hit?.label ?? 'Portal';
}

/** The tenant portal shell: semantic header/nav/main landmarks + a skip link + logout. */
export function PortalLayout() {
  const { principal, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // SPA route change (WCAG 2.4.3 Focus Order): move keyboard focus onto <main> — off the stale
  // nav link — and announce the new page via the visually-hidden aria-live region below, so
  // screen-reader users get the same "page changed" signal a full navigation would give them.
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);

  return (
    <>
      <SkipLink />
      <header className="app-header">
        <p className="brand">Garage Band LLC</p>
        <nav className="app-nav" aria-label="Tenant portal">
          <ul>
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} end={n.end}>
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <span className="header-spacer" />
        <div className="row">
          {principal && <span className="muted">{principal.displayName}</span>}
          <button
            type="button"
            className="secondary"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} ref={mainRef}>
        <Outlet />
      </main>
      <div className="visually-hidden" role="status" aria-live="polite">
        {pageTitle(location.pathname)} page loaded
      </div>
    </>
  );
}
